import { Shield } from 'lucide-react';
import type { RiskAssessmentPayload } from '@/lib/analyticsApi';

interface RiskPanelProps {
  risk: RiskAssessmentPayload | undefined;
  loading?: boolean;
  fallbackMetrics?: {
    rampRateCtlPerWeek?: number;
    tsb?: number;
  };
}

const riskColors = {
  low: { text: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-400/20' },
  moderate: { text: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-400/20' },
  high: { text: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/20' },
};

const bandToVisual = {
  normal: 'low',
  elevated: 'moderate',
  high: 'high',
  critical: 'high',
} as const;

const loadStatusByBand = {
  normal: 'balanced',
  elevated: 'building strain',
  high: 'fatigue heavy',
  critical: 'recovery priority',
} as const;

export function RiskPanel({ risk, loading = false, fallbackMetrics }: RiskPanelProps) {
  const band = risk?.risk_band ?? 'normal';
  const isSyncing = (risk?.drivers ?? []).some((d) => d.toLowerCase().includes('sync'));
  const visual = bandToVisual[band];
  const colors = riskColors[visual];
  const strainScore = risk?.strain_score;
  const hasStrain = typeof strainScore === 'number' && Number.isFinite(strainScore);
  const explanation = risk?.explanation;
  const drivers = (risk?.drivers ?? []).slice(0, 4);

  const rampRateValue = Number.isFinite(risk?.metrics?.ramp_rate as number)
    ? (risk?.metrics.ramp_rate as number)
    : (fallbackMetrics?.rampRateCtlPerWeek ?? Number.NaN);
  const tsbValue = Number.isFinite(risk?.metrics?.tsb as number)
    ? (risk?.metrics.tsb as number)
    : (fallbackMetrics?.tsb ?? Number.NaN);

  return (
    <div className={`rounded-xl border p-4 ${colors.border} ${colors.bg}`}>
      <div className="flex items-center gap-2 mb-3">
        <Shield className={`h-4 w-4 ${colors.text}`} />
        <h3 className="text-sm font-semibold">Risk Assessment</h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <Metric label="Risk" value={isSyncing ? 'syncing' : band} color={colors.text} />
        {hasStrain ? (
          <Metric
            label="Strain"
            value={`${(strainScore as number).toFixed(0)}/100`}
            color={(strainScore as number) >= 75 ? 'text-red-400' : (strainScore as number) >= 55 ? 'text-amber-400' : 'text-emerald-400'}
          />
        ) : (
          <Metric label="Load Status" value={loadStatusByBand[band]} color={colors.text} />
        )}
        <Metric
          label="Ramp Rate"
          value={Number.isFinite(rampRateValue) ? `${rampRateValue >= 0 ? '+' : ''}${rampRateValue.toFixed(1)}` : '—'}
          color={rampRateValue > 12 ? 'text-red-400' : rampRateValue > 8 ? 'text-amber-400' : 'text-emerald-400'}
        />
        <Metric
          label="Form (TSB)"
          value={Number.isFinite(tsbValue) ? tsbValue.toFixed(1) : '—'}
          color={tsbValue > 5 ? 'text-emerald-400' : tsbValue > -10 ? 'text-amber-400' : 'text-red-400'}
        />
      </div>

      <div className="mb-3 p-3 rounded-lg bg-background/50 border border-border/40">
        <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Why</div>
        <ul className="text-xs text-muted-foreground space-y-1">
          {drivers.length > 0 ? drivers.map((driver, idx) => <li key={`${driver}-${idx}`}>• {driver}</li>) : <li>• Load signals are still stabilizing.</li>}
        </ul>
      </div>

      <div className="p-3 rounded-lg bg-background/50 border border-border/40">
        <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">Coach Recommendation</div>
        {loading ? (
          <p className="text-xs text-muted-foreground leading-relaxed">
            Reading your training signal... dialing in the next move.
          </p>
        ) : (
          <div className="space-y-1.5 text-xs text-muted-foreground leading-relaxed">
            <p>{explanation?.headline || risk?.recommended_action || 'Stay consistent and monitor recovery.'}</p>
            {explanation?.summary && <p>{explanation.summary}</p>}
            {Array.isArray(explanation?.drivers) && explanation.drivers.length > 0 && (
              <ul className="space-y-1">
                {explanation.drivers.slice(0, 4).map((item, idx) => (
                  <li key={`${item}-${idx}`}>• {item}</li>
                ))}
              </ul>
            )}
            {explanation?.next_48h && <p><span className="text-foreground/80">Next 48h:</span> {explanation.next_48h}</p>}
            {explanation?.next_7d && <p><span className="text-foreground/80">Next 7d:</span> {explanation.next_7d}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div>
      <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-sm font-bold tabular-nums capitalize ${color}`}>{value}</div>
    </div>
  );
}
